import { FC } from 'react';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuItem,
} from '@repo/ui/components/ui/dropdown-menu';
import { Button } from '@repo/ui/components/ui/button';
import {
  useCandidate,
  useCandidates,
  useCandidateSelection,
  useStages,
  useUser,
} from '@/state';
import { OpenCallStage } from '@repo/api/open-calls/graphql';
import { permissions } from '@repo/api/permify/constants';
import { ConfirmationDialog } from '@repo/ui/components/ui/confirmation-dialog';
import { spUserNavigation } from '@/helpers/snowplow';

type MoveCandidateProps = {
  className?: string;
  candidateIds: Array<string>;
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  onOpenChange?: (open: boolean) => void;
  onMoveCompleted?: () => void;
};

export const MoveCandidateToStage: FC<MoveCandidateProps> = ({
  className,
  candidateIds,
  variant = 'default',
  onOpenChange,
  onMoveCompleted,
}) => {
  const { stages, changeCandidateCount } = useStages();
  const candidate = useCandidate(candidateIds[0] as string);
  const {
    params: {
      filters: { stageId, openCallId },
    },
    moveToStage,
  } = useCandidates();
  const { deselectAll } = useCandidateSelection();

  const session = useUser();
  const remaining = (stages as OpenCallStage[]).filter(
    (stage) => stage.id !== stageId && !stage.name.includes('Dismissed'),
  );
  const findStage = (id: string) => stages.find((stage) => stage.id === id);
  const moveCandidates = (targetStageId: string) => async () => {
    changeCandidateCount({
      [stageId]: findStage(stageId)!.count - candidateIds.length,
      [targetStageId]: findStage(targetStageId)!.count + candidateIds.length,
    });
    await moveToStage(candidateIds, targetStageId, session?.user?.sub || '');
    const targetStage = findStage(targetStageId);
    spUserNavigation(
      candidateIds,
      targetStage as unknown as Record<string, string>,
    );

    deselectAll();

    const toastMessage = (
      <span className="text-sm font-normal text-cn-foreground">
        {candidateIds.length === 1
          ? candidate?.profile.firstName
          : candidateIds.length + ' candidates'}{' '}
        moved to{' '}
        <a
          className="underline"
          href={`/open-calls/${openCallId}/stage/${targetStageId}`}
        >
          {targetStage?.name}.
        </a>
      </span>
    );

    toast(
      <>
        {toastMessage}
        {/* TODO: Enable `Undo` button after MVP */}
        <Button variant="outline" className="hidden ml-4">
          Undo
        </Button>
      </>,
      {
        className: 'pl-6 pr-8 py-6',
      },
    );
    if (onMoveCompleted) {
      onMoveCompleted();
    }
  };
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          className={className}
          data-permission={permissions.openCalls.moveCandidates}
        >
          Move to
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="rounded-none w-[calc(var(--radix-popper-anchor-width))] z-50">
        <DropdownMenuGroup>
          {remaining.map((stage) => (
            <DropdownMenuItem key={'stage_key_' + stage.id} asChild>
              {candidateIds.length >= 7 ? (
                <ConfirmationDialog
                  title="Move candidates?"
                  key={'stage_key_dialog' + stage.id}
                  description={`${candidateIds.length} candidates will be moved to ${stage.name}.`}
                  permission={permissions.openCalls.moveCandidates}
                  actionName="Move"
                  action={moveCandidates(stage.id)}
                >
                  <Button
                    variant="ghost"
                    className="w-full relative flex cursor-default select-none items-center gap-2 px-2 text-sm
                      outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none
                      data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0 rounded-none justify-start font-normal"
                  >
                    {stage.name}
                  </Button>
                </ConfirmationDialog>
              ) : (
                <Button
                  variant="ghost"
                  className="w-full relative flex cursor-default select-none items-center gap-2 px-2 text-sm
                    outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none
                    data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0 rounded-none justify-start font-normal"
                  onClick={moveCandidates(stage.id)}
                >
                  {stage.name}
                </Button>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
